// Rooster-logica van de Planner To-Do: kolommen (incl. weekenden), balkplaatsing op
// een persoonsrij, week-totalen en kleuren. Puur — geen React — zodat het te testen is.
//
// Waarom een eigen module: de planner zette taakbalken op pixelposities
// (kolom × dagbreedte) met vaste breedtes. Dat dwong vaste kolommen af, waardoor het
// raster rechts leeg doorliep, en weekenden bestonden niet. Nu is elke dag een
// grid-kolom (weekenden smal) en krijgen balken een kolombereik — het raster vult de
// breedte en balken lopen vanzelf mee.

import { effectiveTaskHours, DEFAULT_CAPACITY_RULES, isVanalles, DAGSTAAT } from '../../utils/capacityCalc';
import { AFGERONDE_STATUSSEN } from '../../utils/taakStatus';

export const DAGNAMEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
export const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const pad = (n) => String(n).padStart(2, '0');
export const isoVan = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const dagDeel = (v) => String(v || '').slice(0, 10);

function isoWeek(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jan4 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - jan4) / 864e5 - 3 + ((jan4.getDay() + 6) % 7)) / 7);
}

/**
 * Alle kalenderdagen van het venster (maandag t/m zondag × weken).
 * @returns [{ iso, date, dagNaam, dagNr, maand, weekend, maandag, weekNr, eersteVanMaand }]
 */
export function maakKolommen(weekStart, weken = 2) {
  const uit = [];
  const start = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
  for (let i = 0; i < weken * 7; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const dow = d.getDay();
    uit.push({
      iso: isoVan(d),
      date: d,
      dagNaam: DAGNAMEN[dow],
      dagNr: d.getDate(),
      maand: MAANDEN[d.getMonth()],
      weekend: dow === 0 || dow === 6,
      maandag: dow === 1,
      weekNr: isoWeek(d),
      eersteVanMaand: d.getDate() === 1 || i === 0,
    });
  }
  return uit;
}

/** Minimale breedte van een werkdag-kolom; minder weken = bredere, beter sleepbare dagen. */
export const MIN_DAG_BREEDTE = { 1: 96, 2: 52, 4: 26 };
/** Weekenden zijn smal en gearceerd: zichtbaar als pauze, maar geen verspilde ruimte. */
export const WEEKEND_BREEDTE = { 1: 14, 2: 8, 4: 5 };

/** grid-template-columns voor de dagkolommen (zonder naamkolom). */
export function kolomSjabloon(kolommen, weken = 2) {
  const min = MIN_DAG_BREEDTE[weken] || 40;
  const wk = WEEKEND_BREEDTE[weken] || 12;
  return kolommen.map((k) => (k.weekend ? `${wk}px` : `minmax(${min}px, 1fr)`)).join(' ');
}

/** Hoeveel werkdagen een taak inneemt bij het inplannen (8u = 1 dag, min. 1). */
export function werkdagenVoor(task, rules = DEFAULT_CAPACITY_RULES) {
  const { val } = effectiveTaskHours(task, rules);
  return Math.max(1, Math.ceil(val / (rules.hoursPerDay || 8)));
}

/**
 * Kolombereik [c0, c1] (inclusief, indexen in `kolommen`) voor een datumbereik.
 * Begint of eindigt het in een weekend, dan schuift het naar de werkdag ernaast.
 * Valt het helemaal buiten beeld: null.
 * `weekendNaarWerkdag: false` (agenda-afspraken, draaidagen): iets dat alleen in een
 * weekend valt krijgt géén werkdag — null. Die staan als markering in de weekendkolom
 * (zie weekendWerk). Zonder dat stond "TEN MILES" (zo 27 sep) op maandag (Joris, 25 sep).
 */
export function kolomBereik(startISO, eindISO, kolommen, { weekendNaarWerkdag = true } = {}) {
  if (!kolommen.length) return null;
  const s = dagDeel(startISO);
  const e = dagDeel(eindISO || startISO) || s;
  if (!s) return null;
  const eerste = kolommen[0].iso;
  const laatste = kolommen[kolommen.length - 1].iso;
  if (e < eerste || s > laatste) return null;
  let c0 = kolommen.findIndex((k) => !k.weekend && k.iso >= s);
  let c1 = -1;
  for (let i = kolommen.length - 1; i >= 0; i -= 1) {
    if (!kolommen[i].weekend && kolommen[i].iso <= e) { c1 = i; break; }
  }
  if (c0 === -1 && c1 === -1) return null;
  if (!weekendNaarWerkdag && (c0 === -1 || c1 === -1 || c1 < c0)) return null;
  if (c0 === -1) c0 = c1;
  if (c1 === -1 || c1 < c0) {
    // Alleen weekend (of begint in het laatste weekend): één werkdag breed tonen.
    c1 = c0;
  }
  return { c0, c1, afgekaptLinks: s < eerste, afgekaptRechts: e > laatste };
}

/** Verdeel geplaatste balken over lanes zodat ze elkaar niet overlappen. */
export function verdeelOverLanes(items) {
  const lanes = []; // per lane: eerstvolgende vrije kolom
  const geplaatst = [...items]
    .sort((a, b) => a.c0 - b.c0 || (b.c1 - b.c0) - (a.c1 - a.c0))
    .map((item) => {
      let lane = lanes.findIndex((vrij) => vrij <= item.c0);
      if (lane === -1) { lane = lanes.length; lanes.push(0); }
      lanes[lane] = item.c1 + 1;
      return { ...item, lane };
    });
  return { geplaatst, laneCount: lanes.length };
}

/**
 * Lanes per project (Dagstart, Joris 6 okt: "zelfde project op één regel"). Alle blokken met
 * dezelfde `sleutel` krijgen samen één vaste lane over het hele venster, ook als ze los van
 * elkaar liggen (bv. 9689 op ma–di en nog 2 u op vr). Andere projecten vullen de gaten in die
 * lane en de lanes eronder. Overlappen blokken van hetzelfde project toch (uitgeklapte losse
 * taken), dan krijgt dat project een tweede lane direct eronder — nooit verspreid.
 * Volgorde: project dat het eerst begint eerst, bij gelijke start het breedste.
 */
export function verdeelOverLanesPerProject(items) {
  const groepen = new Map();
  (items || []).forEach((it, i) => {
    const k = it.sleutel ?? `los:${i}`;
    if (!groepen.has(k)) groepen.set(k, []);
    groepen.get(k).push(it);
  });
  const bezet = []; // per lane: [[c0, c1], ...]
  const vrij = (lane, it) => !(bezet[lane] || []).some(([a, b]) => it.c0 <= b && it.c1 >= a);
  const past = (sub, lane) => sub.every(({ it, r }) => vrij(lane + r, it));
  const lijst = [...groepen.values()].map((g) => {
    const opVolgorde = [...g].sort((a, b) => a.c0 - b.c0 || (b.c1 - b.c0) - (a.c1 - a.c0));
    // Binnen het project: overlappende stukken op sub-rijen (meestal maar één).
    const subEinde = [];
    const sub = opVolgorde.map((it) => {
      let r = subEinde.findIndex((e) => e < it.c0);
      if (r === -1) { r = subEinde.length; subEinde.push(-1); }
      subEinde[r] = it.c1;
      return { it, r };
    });
    const c0 = opVolgorde[0].c0;
    const span = Math.max(...opVolgorde.map((x) => x.c1)) - c0;
    return { sub, c0, span };
  }).sort((a, b) => a.c0 - b.c0 || b.span - a.span);

  const geplaatst = [];
  lijst.forEach(({ sub }) => {
    let lane = 0;
    while (!past(sub, lane)) lane += 1;
    sub.forEach(({ it, r }) => {
      const l = lane + r;
      if (!bezet[l]) bezet[l] = [];
      bezet[l].push([it.c0, it.c1]);
      geplaatst.push({ ...it, lane: l });
    });
  });
  // Zelfde volgorde als verdeelOverLanes (op startkolom), handig voor tests en tabvolgorde.
  geplaatst.sort((a, b) => a.c0 - b.c0 || a.lane - b.lane);
  return { geplaatst, laneCount: bezet.length, perProject: true };
}

const UITGESLOTEN = new Set(AFGERONDE_STATUSSEN);

/**
 * Hoort deze taak als balk op iemands rij? Projectwerk en draaidagen wel; meetings
 * (komen uit de agenda), vrije dagen (zijn een dagstaat) en vanallesuurtjes (klein,
 * elke dag, telt wél mee in de bezetting maar zou de rij dichtslibben) niet.
 */
export function isBalkTaak(t) {
  if (!t || !t.startDate) return false;
  if (t.type === 'Afspraak' || t.type === 'Vrije dag') return false;
  if (isVanalles(t)) return false;
  if (t.isComplete === true) return false;
  return !UITGESLOTEN.has(t.prio || t.status || '');
}

/** Balken voor een persoonsrij: echte start–eind-datum. */
export function persoonBalken(tasks, kolommen) {
  const items = [];
  (tasks || []).filter(isBalkTaak).forEach((task) => {
    const b = kolomBereik(task.startDate, task.endDate, kolommen);
    if (b) items.push({ task, ...b, ongepland: false });
  });
  return verdeelOverLanes(items);
}

/** Draaidag (uit de draaidagenbron) of grote agenda-afspraak: vast blok, niet sleepbaar. */
export const isVastBlok = (t) => !!(t?.bronDraaidag || t?.bronAgenda);

/** Sleutel waarop taken tot één projectblok samenvallen. */
export const projectSleutel = (t) => {
  if (t?.bronDraaidag) return `draaidag:${t.draaidagId || t.id}`;
  if (t?.bronAgenda) return t.agendaSleutel || `agenda:${t.id}`;
  return t?.projectId || t?.projectNumber || t?.project || `taak:${t?.id}`;
};

/**
 * Projectblokken voor een persoonsrij: taken van hetzelfde project die elkaar raken of
 * overlappen (weekenden tellen als brug) worden één blok, zoals een planner het leest:
 * "wie werkt wanneer aan wélk project". Een blok houdt zijn taken vast, zodat slepen
 * alles tegelijk kan verschuiven.
 * `laneProProject` (Dagstart): elk project houdt één vaste lane, zie verdeelOverLanesPerProject.
 * `groep(task)` (Dagstart): taken van één project alleen samenvoegen als hun groep gelijk is.
 */
export function persoonBlokken(tasks, kolommen, { laneProProject = false, groep = null } = {}) {
  const perProject = new Map();
  (tasks || []).filter(isBalkTaak).forEach((task) => {
    // Vaste blokken (agenda, draaidag) die alleen in het weekend vallen: niet naar maandag.
    const b = kolomBereik(task.startDate, task.endDate, kolommen, { weekendNaarWerkdag: !isVastBlok(task) });
    if (!b) return;
    // `groep` (Dagstart: soort werk) splitst een project in blokken per groep; de lane blijft
    // per project (sleutel), zodat script en animatie van één project op dezelfde regel staan.
    const k = groep ? `${projectSleutel(task)}\u0000${groep(task)}` : projectSleutel(task);
    if (!perProject.has(k)) perProject.set(k, []);
    perProject.get(k).push({ task, ...b });
  });
  const alleenWeekend = (van, tot) => {
    for (let i = van; i <= tot; i += 1) if (!kolommen[i]?.weekend) return false;
    return true;
  };
  const blokken = [];
  perProject.forEach((items, groepSleutel) => {
    const sleutel = groep ? projectSleutel(items[0].task) : groepSleutel;
    items.sort((a, b) => a.c0 - b.c0 || a.c1 - b.c1);
    let huidig = null;
    items.forEach((it) => {
      const raakt = huidig && (it.c0 <= huidig.c1 + 1 || alleenWeekend(huidig.c1 + 1, it.c0 - 1));
      if (raakt) {
        huidig.c1 = Math.max(huidig.c1, it.c1);
        huidig.tasks.push(it.task);
        huidig.afgekaptLinks = huidig.afgekaptLinks || it.afgekaptLinks;
        huidig.afgekaptRechts = huidig.afgekaptRechts || it.afgekaptRechts;
      } else {
        huidig = { sleutel, c0: it.c0, c1: it.c1, tasks: [it.task], afgekaptLinks: it.afgekaptLinks, afgekaptRechts: it.afgekaptRechts };
        blokken.push(huidig);
      }
    });
  });
  blokken.forEach((b) => {
    b.task = b.tasks[0];
    b.id = `${b.sleutel}:${b.tasks.map((t) => t.id).join('+')}`;
    b.vast = isVastBlok(b.task);
  });
  return laneProProject ? verdeelOverLanesPerProject(blokken) : verdeelOverLanes(blokken);
}

/** "2026-09-28" → "ma 28 sep" (leesbaarder dan de ISO-datum in tooltips). */
export function dagDatum(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dagDeel(iso));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `${DAGNAMEN[d.getDay()]} ${d.getDate()} ${MAANDEN[d.getMonth()]}`;
}

/** Kort taaklabel binnen een projectblok: "Montage DEF - Manifestfilm …" → "Montage DEF". */
export const kortTaak = (naam) => String(naam || '').split(' - ')[0].trim() || String(naam || '');

/**
 * Korte labels voor de taken in één blok: het gedeelde begin eraf, zodat "Montage v intern",
 * "Montage v1" en "Montage v2" in een smal segment als "v intern", "v1", "v2" leesbaar zijn.
 * Er blijft altijd minstens één woord over; zonder gedeeld begin blijft het label heel.
 */
export function segmentLabels(namen) {
  const woorden = (namen || []).map((n) => kortTaak(n).split(/\s+/).filter(Boolean));
  if (woorden.length < 2) return woorden.map((w) => w.join(' '));
  return zonderGedeeldBegin(woorden).map((w) => w.join(' '));
}

/** Woordlijsten zonder het gedeelde begin; er blijft altijd minstens één woord over. */
function zonderGedeeldBegin(woorden) {
  if (woorden.length < 2) return woorden;
  let gedeeld = 0;
  const gelijk = (i) => woorden.every((w) => w.length > i + 1
    && w[i].toLowerCase() === woorden[0][i].toLowerCase());
  while (gelijk(gedeeld)) gedeeld += 1;
  return woorden.map((w) => w.slice(gedeeld));
}

const NUMMERING = /^0*(\d+)\s*[.)]\s*(.*)$/;

/**
 * Het onderscheidende stukje uit de rest van de taaknaam ("01. Hero film" → "Hero film"):
 * nummering eraf, en is de tekst daarna bij twee taken gelijk ("01. Film" / "02. Film"), dan
 * het nummer erachter ("Film 1" / "Film 2"). Hoogstens drie woorden.
 */
function onderscheidendeStukken(stukken) {
  const delen = stukken.map((s) => {
    const m = NUMMERING.exec(String(s || '').trim());
    return m ? { tekst: m[2].trim(), nr: String(Number(m[1])) } : { tekst: String(s || '').trim(), nr: '' };
  });
  // Eerst het gedeelde begin eraf ("Hero film 16:9" / "Hero film 9:16" → "16:9" / "9:16").
  const woorden = zonderGedeeldBegin(delen.map((d) => d.tekst.split(/\s+/).filter(Boolean)));
  // Afkappen op drie woorden, behalve waar dat twee stukken weer gelijk zou maken.
  const afgekapt = woorden.map((w) => (w.length > 3 ? `${w.slice(0, 3).join(' ')}…` : w.join(' ')));
  const telAf = new Map();
  afgekapt.forEach((t) => { const k = t.toLowerCase(); telAf.set(k, (telAf.get(k) || 0) + 1); });
  const kort = afgekapt.map((t, i) => (telAf.get(t.toLowerCase()) > 1 ? woorden[i].join(' ') : t));
  const tel = new Map();
  kort.forEach((t) => { const k = t.toLowerCase(); tel.set(k, (tel.get(k) || 0) + 1); });
  return kort.map((t, i) => {
    const { nr } = delen[i];
    if (!t) return nr ? `nr ${nr}` : '';
    return nr && tel.get(t.toLowerCase()) > 1 ? `${t} ${nr}` : t;
  });
}

/**
 * Gelijke korte labels onderscheidend maken (wiki Rooster 27 sep, 9721: "Spot | Montage v
 * intern | Montage v intern"). Taken met hetzelfde label krijgen het eerste deel van hun
 * taaknaam waarin ze verschillen erbij: "Montage v intern - 01. Hero film - 30s" en "Montage v
 * intern - Film 2 - 30s" → "v intern · Hero film" en "v intern · Film 2". Het label zelf
 * verliest dan zijn eerste woord als het er drie of meer heeft, zodat het onderscheidende
 * stuk in een smal segment niet achter de ellipsis verdwijnt. Namen die helemaal gelijk zijn
 * blijven zoals ze waren.
 * @param namen  volledige taaknamen
 * @param labels huidige korte labels (zelfde volgorde)
 */
export function onderscheidendeLabels(namen, labels) {
  const uit = [...(labels || [])];
  const perLabel = new Map();
  uit.forEach((l, i) => {
    const k = String(l || '').toLowerCase();
    if (!perLabel.has(k)) perLabel.set(k, []);
    perLabel.get(k).push(i);
  });
  const resten = (namen || []).map((n) => String(n || '').split(' - ').slice(1).map((d) => d.trim()));
  const extra = uit.map(() => []);
  // Het eerste naamdeel waarin deze taken verschillen; blijven er daarna nog gelijk, dan
  // die met het volgende deel verfijnen ("Hero - 16:9" / "Hero - 9:16" / "Teaser").
  const verfijn = (idxs, vanaf) => {
    const lengte = Math.max(...idxs.map((i) => resten[i].length));
    const verschilt = (k) => new Set(idxs.map((i) => (resten[i][k] || '').toLowerCase())).size > 1;
    let j = vanaf;
    while (j < lengte && !verschilt(j)) j += 1;
    if (j >= lengte) return;
    const stukken = onderscheidendeStukken(idxs.map((i) => resten[i][j] || ''));
    const perStuk = new Map();
    idxs.forEach((i, n) => {
      if (stukken[n]) extra[i].push(stukken[n]);
      const k = stukken[n].toLowerCase();
      if (!perStuk.has(k)) perStuk.set(k, []);
      perStuk.get(k).push(i);
    });
    perStuk.forEach((groep) => { if (groep.length > 1) verfijn(groep, j + 1); });
  };
  perLabel.forEach((idxs) => {
    if (idxs.length < 2) return;
    verfijn(idxs, 0);
    idxs.forEach((i) => {
      if (!extra[i].length) return;
      const basis = String(uit[i] || '').split(/\s+/).filter(Boolean);
      const kort = basis.length >= 3 ? basis.slice(1).join(' ') : basis.join(' ');
      uit[i] = `${kort} · ${extra[i].join(' · ')}`;
    });
  });
  return uit;
}

/**
 * Een projectblok met meerdere taken in stukken per einddatum (wiki Dagstart 26 sep: "zie ik
 * wanneer v intern en v1 af moeten?"). Elk segment loopt tot de laatste dag van zijn taak/taken
 * en draagt hun korte label; taken die op dezelfde dag af zijn delen één segment ("v1 + v2").
 * Twee segmenten met hetzelfde label krijgen het onderscheidende deel van hun taaknaam erbij
 * (onderscheidendeLabels).
 * Eén segment (alles tegelijk af) of één taak = [] — dan is er niets op te delen.
 * @returns [{ c0, c1, label, eind, taken }] met c0/c1 als kolomindexen (zoals het blok)
 */
export function blokSegmenten(blok, kolommen) {
  const taken = blok?.tasks || [];
  if (taken.length < 2) return [];
  let labels = segmentLabels(taken.map((t) => t.name));
  const perEind = new Map();
  taken.forEach((t, i) => {
    const b = kolomBereik(t.startDate, t.endDate, kolommen);
    const c1 = b ? Math.min(Math.max(b.c1, blok.c0), blok.c1) : blok.c1;
    if (!perEind.has(c1)) perEind.set(c1, { taken: [], idx: [], eind: '' });
    const g = perEind.get(c1);
    g.taken.push(t);
    g.idx.push(i);
    const eind = dagDeel(t.endDate || t.startDate);
    if (eind > g.eind) g.eind = eind;
  });
  if (perEind.size < 2) return [];
  const groepen = [...perEind.keys()].sort((a, b) => a - b).map((c1) => ({ c1, ...perEind.get(c1) }));
  const labelVan = (g) => [...new Set(g.idx.map((i) => labels[i]).filter(Boolean))].join(' + ');
  // Botsen twee segmenten op hun label, dan de taken daarin onderscheidend maken.
  const tel = new Map();
  groepen.forEach((g) => { const l = labelVan(g).toLowerCase(); tel.set(l, (tel.get(l) || 0) + 1); });
  const botsend = groepen.filter((g) => tel.get(labelVan(g).toLowerCase()) > 1).flatMap((g) => g.idx);
  if (botsend.length) {
    const beter = onderscheidendeLabels(botsend.map((i) => taken[i].name), botsend.map((i) => labels[i]));
    labels = [...labels];
    botsend.forEach((i, n) => { labels[i] = beter[n]; });
  }
  let van = blok.c0;
  return groepen.map((g) => {
    const seg = { c0: van, c1: g.c1, label: labelVan(g), eind: g.eind, taken: g.taken };
    van = g.c1 + 1;
    return seg;
  });
}

/** Het kolomsjabloon ("minmax(52px, 1fr) 8px …") als losse kolommen, om een deel te kunnen hergebruiken. */
export const sjabloonDelen = (sjabloon) => String(sjabloon || '').match(/minmax\([^)]*\)|[^\s]+/g) || [];

/** Uren van een lijst taken (UrenWerk, anders de inschatting). */
export function urenVan(tasks, rules = DEFAULT_CAPACITY_RULES) {
  return (tasks || []).reduce((s, t) => s + effectiveTaskHours(t, rules).val, 0);
}

/**
 * Balken in de "te plannen"-bak. Een taak met een gewenste startdatum in beeld staat
 * op die dag, even breed als hij werkdagen kost; begon die vóór het venster, dan aan
 * het begin. Zonder bruikbare datum schuiven ze links in (gestreept = nog geen datum).
 */
export function bakBalken(tasks, kolommen, rules = DEFAULT_CAPACITY_RULES) {
  const werkIdx = kolommen.map((k, i) => (k.weekend ? -1 : i)).filter((i) => i >= 0);
  if (!werkIdx.length) return { geplaatst: [], laneCount: 1 };
  const posVan = (iso) => werkIdx.findIndex((i) => kolommen[i].iso >= iso);
  const eerste = kolommen[0].iso;
  const laatste = kolommen[kolommen.length - 1].iso;
  let pak = 0;
  const items = (tasks || []).map((task) => {
    const n = werkdagenVoor(task, rules);
    const s = dagDeel(task.startDate);
    let p;
    let ongepland = false;
    if (s && s <= laatste) {
      p = s < eerste ? 0 : posVan(s);
      if (p === -1) { p = pak; ongepland = true; pak += n; }
    } else {
      p = pak; ongepland = true; pak += n;
    }
    p = Math.min(p, werkIdx.length - 1);
    const q = Math.min(p + n - 1, werkIdx.length - 1);
    return { task, c0: werkIdx[p], c1: werkIdx[q], ongepland, afgekaptLinks: false, afgekaptRechts: p + n - 1 > q };
  });
  const r = verdeelOverLanes(items);
  return { ...r, laneCount: Math.max(1, r.laneCount) };
}

/**
 * Week-totalen van één persoon: geplande uren tegen beschikbare uren.
 * Vrije dagen tellen niet als capaciteit; draaidagen staan al op een volle dag.
 */
export function weekTotaal(uren, staat, dayISOs, hoursPerDay = 8) {
  let gepland = 0;
  let capaciteit = 0;
  let vrijeDagen = 0;
  let overboekt = 0;
  let teveel = 0; // som van de uren bóven een volle dag, over de overboekte dagen
  (dayISOs || []).forEach((d) => {
    if (staat?.[d] === DAGSTAAT.VRIJ) { vrijeDagen += 1; return; }
    const u = Number(uren?.[d]) || 0;
    gepland += u;
    capaciteit += hoursPerDay;
    if (u > hoursPerDay + 0.05) { overboekt += 1; teveel += u - hoursPerDay; }
  });
  const pct = capaciteit ? Math.round((gepland / capaciteit) * 100) : 0;
  return { gepland, capaciteit, ruimte: Math.max(0, capaciteit - gepland), pct, vrijeDagen, overboekt, teveel };
}

/**
 * Uren zoals een planner ze leest: hele uren ("69", niet "69,5" of "68,3") voor
 * totalen, ruimte en dagbezetting. Een klein maar echt bedrag rondt niet weg naar
 * "0": 0,3u wordt "½". Voor losse taken/afspraken: `fmtHalfUren`.
 */
/**
 * Dagtotaal onderin een dag: capaciteit min gepland (Joris 6 okt: ook "+1u" tonen).
 *   − = overboekt (meer werk dan uren), + = nog ruimte. Onder een kwartier: niets (geen "0u").
 * `toonRuimte: false` (Planning → Rooster) laat alleen het tekort zien, zoals voorheen.
 * @returns {{ richting: 'over'|'ruimte', uren: number, tekst: string, uitleg: string } | null}
 */
export function dagSaldo(uren, hoursPerDay = 8, { toonRuimte = true } = {}) {
  const gepland = Number(uren) || 0;
  const saldo = (Number(hoursPerDay) || 0) - gepland;
  if (Math.abs(saldo) < 0.25) return null;
  const n = fmtUren(Math.abs(saldo));
  if (saldo < 0) {
    return {
      richting: 'over', uren: -saldo, tekst: `−${n}u`,
      uitleg: `−${n}u: ${n}u overboekt — ${fmtUren(gepland)}u gepland op ${fmtUren(hoursPerDay)}u beschikbaar`,
    };
  }
  if (!toonRuimte) return null;
  return {
    richting: 'ruimte', uren: saldo, tekst: `+${n}u`,
    uitleg: `+${n}u: nog ${n}u ruimte — ${fmtUren(gepland)}u gepland van ${fmtUren(hoursPerDay)}u beschikbaar`,
  };
}

export function fmtUren(u) {
  const n = Number(u) || 0;
  const heel = Math.round(Math.abs(n));
  if (heel === 0 && Math.abs(n) >= 0.25) return `${n < 0 ? '−' : ''}½`;
  return String(n < 0 && heel ? -heel : heel);
}

/** Losse taak/afspraak: op halve uren, "½", "1½", "6" — daar maakt een half uur verschil. */
export function fmtHalfUren(u) {
  const n = Number(u) || 0;
  const halven = Math.round(Math.abs(n) * 2);
  const heel = Math.floor(halven / 2);
  const tekst = `${heel || (halven % 2 ? '' : '0')}${halven % 2 ? '½' : ''}`;
  return n < 0 && halven ? `−${tekst}` : tekst;
}

// Gedempte, onderling goed te onderscheiden tinten met witte tekst (contrast ≥ 4.5).
export const PROJECT_PALET = [
  '#2563eb', '#0f766e', '#7c3aed', '#be185d', '#c2410c',
  '#4d7c0f', '#0369a1', '#9333ea', '#a16207', '#475569',
  '#15803d', '#b91c1c',
];

function hash(s) {
  let h = 0;
  const str = String(s || '');
  for (let i = 0; i < str.length; i += 1) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Vaste kleur per project (zelfde project = zelfde kleur, in de bak én op de rij). */
export function projectKleur(task) {
  if (task?.type === 'Draaidag' || (task?.prio || task?.status) === 'Draaidag') return '#ea580c';
  const sleutel = task?.projectId || task?.projectNumber || task?.project || task?.projectName;
  if (!sleutel) return '#64748b';
  return PROJECT_PALET[hash(sleutel) % PROJECT_PALET.length];
}

/** Donkere tekst op een pastel projectvlak (contrast ruim boven 4.5:1). */
export const PASTEL_TEKST = '#2a2f3a';
/** Hoeveel projectkleur het vlak krijgt; de rest is wit. */
export const PASTEL_DEKKING = 0.14;
// Draaidag: oranje zoals in Airtable (wiki Dagstart 26 sep), even licht als een projectvlak.
export const DRAAIDAG_ORANJE = '#ea580c';
const DRAAIDAG_VLAK = '#fce8dd'; // tint(DRAAIDAG_ORANJE, PASTEL_DEKKING)
const DRAAIDAG_STREEP = DRAAIDAG_ORANJE;

/**
 * Rust in het rooster (Joris, 25 sep: "kunnen de kleuren wat subtieler?"): een projectblok
 * is een licht pastelvlak (projectkleur op ±14 % dekking) met donkere tekst en een dunne
 * streep links in de volle projectkleur. Deterministisch per project, net als projectKleur.
 * @returns {{ vlak: string, streep: string, tekst: string }}
 */
export function projectStijl(task) {
  if (task?.type === 'Draaidag' || (task?.prio || task?.status) === 'Draaidag') {
    return { vlak: DRAAIDAG_VLAK, streep: DRAAIDAG_STREEP, tekst: PASTEL_TEKST };
  }
  const vol = projectKleur(task);
  return { vlak: tint(vol, PASTEL_DEKKING), streep: vol, tekst: PASTEL_TEKST };
}

/** Relatieve luminantie (WCAG) van een #rrggbb-kleur. */
export function luminantie(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return 1;
  const n = parseInt(m[1], 16);
  const lin = (v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}

/** WCAG-contrastverhouding tussen twee kleuren (1–21). */
export function contrast(a, b) {
  const [l1, l2] = [luminantie(a), luminantie(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

/** Initialen voor de avatar: "Bram van Kaauwen" → "BK". */
export function initialen(naam) {
  const delen = String(naam || '').replace(/\(.*?\)/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!delen.length) return '?';
  const kleine = new Set(['van', 'de', 'der', 'den', 'het', 'ten', 'ter', 'te', "'t"]);
  const achter = [...delen.slice(1)].reverse().find((w) => !kleine.has(w.toLowerCase()));
  return (delen[0][0] + (achter ? achter[0] : '')).toUpperCase();
}

const AVATAR_TINTEN = ['#dbeafe', '#dcfce7', '#fce7f3', '#fef3c7', '#ede9fe', '#cffafe', '#ffedd5', '#e0e7ff'];
const AVATAR_TEKST = ['#1e40af', '#166534', '#9d174d', '#92400e', '#5b21b6', '#155e75', '#9a3412', '#3730a3'];
export function avatarKleur(sleutel) {
  const i = hash(sleutel) % AVATAR_TINTEN.length;
  return { bg: AVATAR_TINTEN[i], tekst: AVATAR_TEKST[i] };
}

/** Eerste twee functies, leesbaar ("Editor · Cameraman"); array of komma-string. */
export function functieTekst(p) {
  const raw = p?.function || p?.Functie || '';
  const str = Array.isArray(raw) ? raw.join(', ') : String(raw);
  return str.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 2).join(' · ');
}

/**
 * Eén stille rolregel: type (Freelancer/Stagiair) + functies, zonder dubbele woorden
 * ("Stagiair · Stagiair · Editor" → "Stagiair · Editor"), hoogstens twee.
 */
export function rolTekst(p) {
  const typeRaw = Array.isArray(p?.type) ? p.type.join(', ') : String(p?.type || '');
  const type = /Freelancer|Bedrijf/.test(typeRaw) ? 'Freelancer' : /Stagiair/.test(typeRaw) ? 'Stagiair' : '';
  const raw = p?.function || p?.Functie || '';
  const functies = (Array.isArray(raw) ? raw.join(', ') : String(raw)).split(',').map((s) => s.trim()).filter(Boolean);
  const gezien = new Set();
  return [type, ...functies].filter((w) => {
    const k = w.toLowerCase();
    if (!w || gezien.has(k)) return false;
    gezien.add(k);
    return true;
  }).slice(0, 2).join(' · ');
}

// ─── Agenda: afwezigheid en "wat vult deze dag nog meer" ─────────────────────

const AFWEZIG_ONDERWERP = /vakantie|verlof|vrije?\s*dag|\bvrij\b|\bziek|afwezig|\bholiday|\bvacation/i;

/** Lijkt dit agenda-onderwerp op afwezigheid ("VAKANTIEEE", "Verlof", "Vrij")? */
export const isAfwezigOnderwerp = (s) => AFWEZIG_ONDERWERP.test(String(s || ''));

/**
 * Dagen waarop iemand volgens de agenda afwezig is. De engine markeert hele-dag-
 * afwezigheid al (`afwezig`), maar een vakantie als gewone afspraak van 08:30–17:00
 * kwam binnen als 8,5 uur "meeting" — en dan stond iemand op zijn vrije dag rood als
 * overboekt (Jur, wk 39). Zo'n bijna-hele-dag-afspraak met een vakantie-onderwerp
 * telt daarom ook als afwezig.
 */
export function afwezigeDagen(afwezigMap, events, hoursPerDay = 8) {
  const dagen = new Set(Object.keys(afwezigMap || {}));
  const perDag = {};
  (events || []).forEach((ev) => {
    if (!ev?.date || !isAfwezigOnderwerp(ev.subject)) return;
    (perDag[ev.date] = perDag[ev.date] || []).push(ev);
  });
  Object.entries(perDag).forEach(([d, lijst]) => { if (afwezigUren(lijst) >= hoursPerDay * 0.75) dagen.add(d); });
  return [...dagen].sort();
}

const minuten = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Het werkdagvenster waarbinnen de engine agenda-uren telt (CAPACITEIT_VENSTER_UUR in
 * mail_assistant_routes.py: 08:00–18:00). De `hours` van een afspraak zijn al op dit
 * venster afgekapt; de tijden (`start_time`/`end_time`) niet. De planningsregels kennen
 * (nog) geen werkdagbegin/-eind, dus volgen we de engine.
 */
export const WERKDAG_VENSTER = { begin: 8 * 60, eind: 18 * 60 };

/**
 * Uren afwezigheid op één dag, met overlappende afspraken samengevoegd: twee keer
 * "Verlof 09:00–12:00" is 3 uur, niet 6. Elk tijdvak wordt eerst zelf op de werkdag
 * geknipt (zoals de engine zijn `hours` knipt) en pas dán samengevoegd — anders telden
 * twee keer "Verlof 07:00–13:00" (elk 5u binnen de werkdag) als 6 uur samen en werd een
 * halve dag ineens een hele (Codex, 25 sep). Een samengevoegd vak telt nooit meer dan de
 * som van zijn `hours`. Afspraken zonder bruikbare tijden (of over middernacht) tellen
 * met hun `hours`.
 * @param venster { begin, eind } in minuten na middernacht; standaard WERKDAG_VENSTER
 */
export function afwezigUren(events, venster = WERKDAG_VENSTER) {
  const vb = Number.isFinite(venster?.begin) ? venster.begin : WERKDAG_VENSTER.begin;
  const ve = Number.isFinite(venster?.eind) ? venster.eind : WERKDAG_VENSTER.eind;
  const vakken = [];
  let los = 0;
  (events || []).forEach((ev) => {
    const u = Math.max(0, Number(ev?.hours) || 0);
    const s = minuten(ev?.start_time);
    const e = minuten(ev?.end_time);
    if (s === null || e === null || e <= s) { los += u; return; }
    const ks = Math.max(s, vb);
    const ke = Math.min(e, ve);
    if (ke <= ks) return; // valt helemaal buiten de werkdag
    vakken.push({ s: ks, e: ke, u });
  });
  vakken.sort((a, b) => a.s - b.s);
  let totaal = 0;
  let huidig = null;
  const sluit = () => { if (huidig) totaal += Math.min((huidig.e - huidig.s) / 60, huidig.u); };
  vakken.forEach((v) => {
    if (huidig && v.s <= huidig.e) {
      huidig.e = Math.max(huidig.e, v.e);
      huidig.u += v.u;
    } else {
      sluit();
      huidig = { ...v };
    }
  });
  sluit();
  return totaal + los;
}

/** Vanaf zoveel uur krijgt een agenda-afspraak een eigen (stil) blok op de rij. */
export const AGENDA_BLOK_MIN_UREN = 3;

const kaalOnderwerp = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Grote agenda-afspraken (≥ 3 u) als blokken, zodat je zonder hover ziet waar iemand
 * mee bezig is. Kleinere blijven alleen de grijze vulling + hover. Niet als blok:
 * privé-afspraken (het onderwerp gaat nooit de deur uit), afwezigheid (dat is de dag-
 * staat "Afwezig"), afgezegde/afwezige dagen, en een afspraak die dezelfde draaidag is
 * als een draaidagblok van deze persoon (dan alleen het draaidagblok).
 * @param events  [{ date, subject, start_time, end_time, hours, prive }]
 * @param opts.afwezig    ['YYYY-MM-DD'] dagen waarop iemand afwezig is
 * @param opts.draaidagen draaidag-taken van deze persoon (zie draaidagTakenPerPersoon)
 * @param opts.zelfde     (onderwerp, draaidagTaak) => boolean
 */
export function agendaBlokTaken(events, {
  afwezig = [], draaidagen = [], zelfde = () => false, minUren = AGENDA_BLOK_MIN_UREN,
} = {}) {
  const weg = new Set(afwezig || []);
  const uit = [];
  (events || []).forEach((ev, i) => {
    const uren = Number(ev?.hours) || 0;
    if (!ev?.date || uren + 1e-9 < minUren) return;
    if (ev.prive || isAfwezigOnderwerp(ev.subject) || weg.has(ev.date)) return;
    if ((draaidagen || []).some((t) => ev.date >= t.startDate && ev.date <= (t.endDate || t.startDate) && zelfde(ev.subject, t))) return;
    const onderwerp = String(ev.subject || '').trim() || 'Afspraak';
    uit.push({
      id: `agenda:${ev.date}:${ev.start_time || ''}:${i}`,
      name: onderwerp,
      type: 'Agenda-afspraak',
      status: 'Agenda',
      startDate: ev.date,
      endDate: ev.date,
      hours: uren,
      bronAgenda: true,
      // Dezelfde afspraak op opeenvolgende dagen wordt één doorlopend blok.
      agendaSleutel: `agenda:${kaalOnderwerp(onderwerp)}`,
      tijd: ev.start_time && ev.end_time ? `${ev.start_time}–${ev.end_time}` : '',
      // Alleen gezet vanuit de Dagstart (afsprakenAlsEvents); voor het afsprakenpaneel.
      ...(ev.soort ? { afspraakSoort: ev.soort } : {}),
      ...(ev.assignee ? { assignee: ev.assignee } : {}),
      ...(ev.afgeschermd ? { afgeschermd: true } : {}),
    });
  });
  return uit;
}

/**
 * Weekendwerk per weekenddag in beeld: agenda-afspraken en draaidagen op za/zo. Het Rooster
 * rekent alleen met werkdagen, dus dit telt niet mee in de capaciteit — het is een stille
 * markering in de smalle weekendkolom, met details in de tooltip.
 * Niet: privé-afspraken, afwezigheid (vakantie in het weekend is geen werk) en een
 * afspraak die dezelfde draaidag is als een draaidag van deze persoon (dan alleen die).
 * @param events     [{ date, subject, start_time, end_time, hours, prive, afgeschermd }]
 * @param draaidagen draaidag-taken van deze persoon (draaidagTakenPerPersoon)
 * @returns { 'YYYY-MM-DD': [{ soort: 'agenda'|'draaidag', tekst, tijd, uren }] }
 */
export function weekendWerk(events, draaidagen, kolommen, { zelfde = () => false } = {}) {
  const weekend = new Set((kolommen || []).filter((k) => k.weekend).map((k) => k.iso));
  const uit = {};
  const zet = (d, item) => { (uit[d] = uit[d] || []).push(item); };
  (draaidagen || []).forEach((t) => {
    const s = dagDeel(t.startDate);
    const e = dagDeel(t.endDate || t.startDate) || s;
    weekend.forEach((d) => {
      if (d >= s && d <= e) zet(d, { soort: 'draaidag', tekst: t.name || 'Draaidag', tijd: '', uren: 0, optie: !!t.draaidagOptie });
    });
  });
  (events || []).forEach((ev) => {
    const d = dagDeel(ev?.date);
    if (!weekend.has(d) || ev.prive || isAfwezigOnderwerp(ev.subject)) return;
    if ((draaidagen || []).some((t) => d >= dagDeel(t.startDate) && d <= dagDeel(t.endDate || t.startDate) && zelfde(ev.subject, t))) return;
    zet(d, {
      soort: 'agenda',
      tekst: ev.afgeschermd ? 'Afspraak' : (String(ev.subject || '').trim() || 'Afspraak'),
      tijd: ev.start_time && ev.end_time ? `${ev.start_time}–${ev.end_time}` : '',
      uren: Number(ev.hours) || 0,
    });
  });
  Object.values(uit).forEach((l) => l.sort((a, b) => (a.soort === 'draaidag' ? -1 : 0) - (b.soort === 'draaidag' ? -1 : 0) || a.tijd.localeCompare(b.tijd)));
  return uit;
}

/** Tooltipregels: "zo 27 sep · TEN MILES 08:30–17:15". */
export function weekendRegels(iso, items) {
  const d = new Date(`${iso}T12:00:00`);
  const dag = `${DAGNAMEN[d.getDay()]} ${d.getDate()} ${MAANDEN[d.getMonth()]}`;
  return (items || []).map((it) => `${dag} · ${it.soort === 'draaidag' ? it.tekst : `${it.tekst}${it.tijd ? ` ${it.tijd}` : ''}`}`);
}

/** Afwezige dagen als 'Vrije dag'-taken, zodat de gedeelde capaciteitsberekening ze als vrij ziet. */
export const afwezigAlsTaken = (dagen) => (dagen || []).map((d) => ({
  id: `afwezig-${d}`, name: 'Afwezig (agenda)', type: 'Vrije dag', status: 'Vrij/intern', startDate: d, endDate: d,
}));

export const AGENDA_LABEL = 'Agenda (Outlook-afspraken)';

/**
 * Wat er op een dag naast de projectblokken staat: agenda-uren en klein werk
 * (vanallesuurtjes, intern). `projectLabels` = labels van de taken die al als blok staan.
 */
export function dagOverig(opbouw, projectLabels = new Set()) {
  let agenda = 0;
  const taken = {};
  (opbouw || []).forEach(({ label, uren }) => {
    if (label === AGENDA_LABEL) { agenda += uren; return; }
    if (projectLabels.has(label) || /^Draaidag/.test(label)) return;
    taken[label] = (taken[label] || 0) + uren;
  });
  const lijst = Object.entries(taken).map(([label, uren]) => ({ label, uren }));
  const klein = lijst.reduce((s, t) => s + t.uren, 0);
  return { agenda, taken: lijst, klein, totaal: agenda + klein };
}

/** Eén regel voor in de cel: "Afspraken 6u · Vanallesuurtje 1u". */
export function overigTekst({ agenda, taken, klein }) {
  const delen = [];
  if (agenda > 0.05) delen.push(`Afspraken ${fmtUren(agenda)}u`);
  if (klein > 0.05) {
    const kaal = (l) => String(l).replace(/^\d+\s·\s/, '');
    delen.push(taken.length === 1 ? `${kaal(taken[0].label)} ${fmtUren(klein)}u` : `Klein werk ${fmtUren(klein)}u`);
  }
  return delen.join(' · ');
}

/** Eerste werkdag (ISO) met een projectblok-taak vanaf `vanafISO`, of null. */
export function eersteTaakDatum(tasksPerPersoon, personIds, vanafISO) {
  let eerste = null;
  (personIds || []).forEach((id) => {
    (tasksPerPersoon?.[id] || []).filter(isBalkTaak).forEach((t) => {
      const eind = dagDeel(t.endDate || t.startDate);
      if (eind < vanafISO) return;
      const s = dagDeel(t.startDate) < vanafISO ? vanafISO : dagDeel(t.startDate);
      if (!eerste || s < eerste) eerste = s;
    });
  });
  return eerste;
}

/** Lichte tint van een hex-kleur (aandeel `a` kleur, rest wit) — voor nog niet ingeplande taken. */
export function tint(hex, a = 0.16) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return '#f3f4f6';
  const n = parseInt(m[1], 16);
  const kanaal = (v) => Math.round(v * a + 255 * (1 - a)).toString(16).padStart(2, '0');
  return `#${kanaal((n >> 16) & 255)}${kanaal((n >> 8) & 255)}${kanaal(n & 255)}`;
}

// ─── Verplaatsen: mede-uitvoerders, uitklappen, uitkomst ─────────────────────

/**
 * Alle uitvoerders van een taak. Levert de engine `assigneeIds`, dan die; anders
 * afgeleid uit de takenlijsten per persoon (/person-tasks-range zet een gedeelde taak
 * bij élke uitvoerder). Null = onbekend: dan geen Assignee overschrijven.
 */
export function uitvoerdersVan(task, tasksPerPersoon, bronPersoonId) {
  if (Array.isArray(task?.assigneeIds) && task.assigneeIds.length) {
    // Tegenstrijdig (de rij waar de taak op staat hoort er niet bij): onbekend.
    if (bronPersoonId && !task.assigneeIds.includes(bronPersoonId)) return null;
    return [...task.assigneeIds];
  }
  const ids = Object.entries(tasksPerPersoon || {})
    .filter(([, lijst]) => (lijst || []).some((t) => t?.id === task?.id))
    .map(([id]) => id);
  if (bronPersoonId && !ids.includes(bronPersoonId)) return null;
  return ids.length ? ids : null;
}

/** Vervang alleen `van` door `naar`; mede-uitvoerders blijven, geen dubbelen. */
export function vervangUitvoerder(ids, van, naar) {
  const uit = [];
  (ids || []).forEach((id) => {
    const nieuw = id === van ? naar : id;
    if (!uit.includes(nieuw)) uit.push(nieuw);
  });
  if (!uit.includes(naar)) uit.push(naar);
  return uit;
}

/**
 * Uitvoerders vóór en ná het verplaatsen van één taak van `bron` naar `naar`.
 * `origineel` = wat undo terugzet (null = onbekend), `nieuw` = wat de PATCH stuurt.
 * Wisselt de persoon en zijn de uitvoerders onbekend, dan `afbreken`: liever niets
 * doen dan mede-uitvoerders wissen.
 */
export function uitvoerdersPlan(task, perPersoon, bron, naar) {
  const wisselt = bron !== naar;
  const origineel = uitvoerdersVan(task, perPersoon, bron);
  return {
    wisselt,
    origineel,
    nieuw: wisselt && origineel ? vervangUitvoerder(origineel, bron, naar) : origineel || [bron],
    afbreken: wisselt && !origineel,
  };
}

/**
 * Optimistische update van de takenlijsten per persoon. `wijzigingen`:
 * [{ task, van: [ids], naar: [ids], startDate, endDate, bekend }] — de taak verdwijnt
 * bij wie hem niet meer heeft en staat met de nieuwe data bij iedereen in `naar`
 * (ook bij een mede-uitvoerder die bleef: zijn taak verschuift mee).
 *
 * `assigneeIds` wordt meegeschreven (= `naar`), anders stuurt een tweede verplaatsing
 * zonder herladen de óude uitvoerders mee (A+B, A→C, dan C→D gaf A+B+D). Is de lijst
 * niet vastgesteld (`bekend: false`), dan vervalt het veld en leidt `uitvoerdersVan`
 * het weer af uit de lijsten per persoon.
 */
export function pasTakenAan(perPersoon, wijzigingen) {
  const uit = { ...(perPersoon || {}) };
  (wijzigingen || []).forEach(({ task, van = [], naar = [], startDate, endDate, bekend = true }) => {
    const { bronPersoonId, assigneeIds, ...schoon } = task;
    void bronPersoonId; void assigneeIds;
    const nieuw = bekend ? { ...schoon, startDate, endDate, assigneeIds: [...naar] } : { ...schoon, startDate, endDate };
    new Set([...van, ...naar]).forEach((pid) => {
      uit[pid] = (uit[pid] || []).filter((t) => t.id !== task.id);
    });
    naar.forEach((pid) => { uit[pid] = [...uit[pid], { ...nieuw }]; });
  });
  return uit;
}

/**
 * Uitklappen: een projectblok met meerdere taken waarvan een taak in `openTaakIds` zit,
 * wordt vervangen door losse balken (elk apart sleepbaar). Lanes opnieuw verdeeld.
 */
export function klapBlokkenUit(blokken, openTaakIds, kolommen) {
  const open = openTaakIds instanceof Set ? openTaakIds : new Set(openTaakIds || []);
  if (!open.size) return blokken;
  const items = [];
  let veranderd = false;
  (blokken?.geplaatst || []).forEach(({ lane, ...blok }) => {
    void lane;
    if (blok.tasks.length < 2 || !blok.tasks.some((t) => open.has(t.id))) { items.push(blok); return; }
    veranderd = true;
    const groep = [];
    blok.tasks.forEach((task) => {
      const b = kolomBereik(task.startDate, task.endDate, kolommen);
      if (!b) return;
      groep.push({
        ...b, sleutel: blok.sleutel, task, tasks: [task], id: `${blok.id}#${task.id}`,
        uitgeklaptUit: blok.tasks.map((t) => t.id),
      });
    });
    // De meest linkse losse taak draagt het samenvoegicoon (één per groep, niet op elke taak).
    const eerste = groep.reduce((min, it) => (!min || it.c0 < min.c0 ? it : min), null);
    groep.forEach((it) => items.push(it === eerste ? { ...it, eersteUitgeklapt: true } : it));
  });
  if (!veranderd) return blokken;
  // Dagstart: ook uitgeklapt blijft een project op zijn eigen lane(s).
  return blokken.perProject ? verdeelOverLanesPerProject(items) : verdeelOverLanes(items);
}

/**
 * Uitkomst van Promise.allSettled over een reeks PATCHes (zelfde volgorde als `plan`):
 * welke lukten (terug te zetten) en een eerlijke melding.
 */
export function verplaatsUitkomst(plan, resultaten) {
  const gelukt = [];
  const mislukt = [];
  (plan || []).forEach((p, i) => ((resultaten?.[i]?.status === 'fulfilled' && resultaten[i].value !== false) ? gelukt : mislukt).push(p));
  const n = (plan || []).length;
  let melding;
  if (!mislukt.length) melding = null;
  else if (!gelukt.length) melding = `Geen van de ${n} taken kon worden verplaatst`;
  else melding = `${gelukt.length} van ${n} taken verplaatst — ${mislukt.length} lukte${mislukt.length === 1 ? '' : 'n'} niet`;
  return { gelukt, mislukt, melding };
}
